import { createRoute, redirect } from '@tanstack/react-router';
import { queryClient } from '@/App.tsx';
import BookingLayout from '@/features/public/booking/components/BookingLayout.tsx';
import BookingPendingSkeleton from '@/features/public/booking/components/BookingPendingSkeleton.tsx';
import BookingErrorPage from '@/features/public/booking/pages/BookingErrorPage.tsx';
import { rootRoute } from '@/routing/rootRoute.ts';
import { getParticipantFormQueryOptions } from '@/features/public/booking/hooks/useGetParticipantForm.ts';
import { getBookingInsurancesQueryOptions } from '@/features/public/booking/hooks/useGetBookingInsurances.ts';
import { getBasicBookingInfoQueryOptions } from '@/features/public/booking/hooks/useGetBasicBookingInfo.ts';
import { RegistrationStatus } from '@/features/dashboard/tours/types/RegistrationStatus.ts';
import { BookingTourBasicDataDto } from '@/features/public/booking/types/BookingTourBasicDataDto.ts';
import {
  bookingViewStage,
  trackFunnelStage,
} from '@/features/public/booking/tracking/funnelTracker.ts';

export type BookingView =
  | { type: 'booking' }
  | { type: 'tour-cancelled' }
  | { type: 'registration-closed' }
  | { type: 'not-open' }
  | { type: 'sold-out' }
  | { type: 'waitlist' };

export const bookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'booking/$orgSlug/$tourSlug',
  beforeLoad: async ({ params }) => {
    const { orgSlug, tourSlug } = params;
    const tourBasicInfo = await queryClient.ensureQueryData(
      getBasicBookingInfoQueryOptions(orgSlug, tourSlug),
    );
    const bookingView = resolveBookingView(tourBasicInfo);

    // Etap leci przed przekierowaniem: inaczej komplet miejsc na wyjeździe
    // z listą rezerwową nigdy by się nie policzył.
    const stage = bookingViewStage(bookingView);
    if (stage) trackFunnelStage(orgSlug, tourSlug, stage);

    if (bookingView.type === 'waitlist') {
      throw redirect({
        to: '/waitlist/$orgSlug/$tourSlug',
        params: { orgSlug, tourSlug },
      });
    }

    return { tourBasicInfo, bookingView };
  },
  loader: async ({ params, context }) => {
    const { orgSlug, tourSlug } = params;
    const { tourBasicInfo, bookingView } = context;

    if (bookingView.type !== 'booking') {
      return {
        tourBasicInfo,
        participantForm: null,
        bookingInsurances: null,
        bookingView,
      };
    }

    const [participantForm, bookingInsurances] = await Promise.all([
      queryClient.ensureQueryData(
        getParticipantFormQueryOptions(orgSlug, tourSlug),
      ),
      queryClient.ensureQueryData(
        getBookingInsurancesQueryOptions(orgSlug, tourSlug),
      ),
    ]);

    return {
      tourBasicInfo,
      participantForm,
      bookingInsurances,
      bookingView,
    };
  },
  component: BookingLayout,
  pendingComponent: BookingPendingSkeleton,
  errorComponent: BookingErrorPage,
});

/**
 * Dwie niezależne osie: `registrationStatus` mówi o oknie zapisów i stanie
 * wyjazdu, zajętość — o wolnych miejscach. Status rozstrzyga pierwszy, bo
 * zamkniętego okna komplet miejsc już nie zmienia.
 */
export function resolveBookingView(
  tourBasicInfo: BookingTourBasicDataDto,
): BookingView {
  const { registrationStatus } = tourBasicInfo.registrationDetails;

  switch (registrationStatus) {
    case RegistrationStatus.CANCELLED:
      return { type: 'tour-cancelled' };
    // Nieogłoszone zapisy wyglądają dla klienta tak samo jak zaplanowane —
    // różni je tylko to, czy jest datę co pokazać.
    case RegistrationStatus.NOT_SCHEDULED:
    case RegistrationStatus.PLANNED:
      return { type: 'not-open' };
    case RegistrationStatus.CLOSED:
      return { type: 'registration-closed' };
    case RegistrationStatus.OPEN:
      break;
  }

  const isFull =
    tourBasicInfo.hasLimit &&
    tourBasicInfo.availableParticipantSpots !== null &&
    tourBasicInfo.availableParticipantSpots <= 0;

  if (isFull) {
    // Na listę rezerwową kierujemy wyłącznie w jej oknie — inaczej klient
    // wypełniłby formularz, który backend odbije `error.waitlist.not_available`.
    return tourBasicInfo.waitlistAvailable
      ? { type: 'waitlist' }
      : { type: 'sold-out' };
  }

  return { type: 'booking' };
}
